/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

// 富文本表格默认边框色
const RICH_TABLE_BORDER_COLOR = '#dcdfe6'
// 富文本表格默认单元格内边距
const RICH_TABLE_CELL_PADDING = '6px 8px'

/**
 * 给富文本中的 table 补充边框内联样式
 *
 * 背景：wangeditor 的表格边框由编辑器自带 CSS 绘制，且带 `.w-e-text-container` 作用域，
 * 生成的 HTML 本身不含任何边框声明。内容脱离编辑器渲染（详情弹窗 / App / H5）时表格线会丢失。
 * 这里在保存前给 table / td / th 注入内联样式，让边框随 HTML 一起持久化，任何渲染环境都能显示。
 *
 * 幂等：已包含 border 声明的元素不会重复处理，重复调用安全。
 */
export function normalizeRichTextTable(html: string): string {
  if (!html || html.indexOf('<table') === -1) {
    return html
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const tables = doc.querySelectorAll('table')
  if (!tables.length) {
    return html
  }

  const appendStyle = (el: Element, cssText: string) => {
    const current = el.getAttribute('style') || ''
    // 已有边框声明则跳过，避免重复注入导致 style 无限增长
    if (/\bborder(-(top|right|bottom|left))?\s*:/i.test(current)) {
      return
    }
    el.setAttribute('style', current ? `${current.replace(/;\s*$/, '')};${cssText}` : cssText)
  }

  tables.forEach((table) => {
    // 部分老旧渲染器（如原生 WebView / TextView）只识别 border 属性，不解析 CSS，一并补上
    table.setAttribute('border', '1')
    table.setAttribute('cellspacing', '0')
    table.setAttribute('cellpadding', '0')
    appendStyle(
      table,
      `border:1px solid ${RICH_TABLE_BORDER_COLOR};border-collapse:collapse;width:100%`
    )

    table.querySelectorAll('td,th').forEach((cell) => {
      appendStyle(
        cell,
        `border:1px solid ${RICH_TABLE_BORDER_COLOR};padding:${RICH_TABLE_CELL_PADDING}`
      )
    })
  })

  return doc.body.innerHTML
}
